import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  getPublicSettings,
  type ApplicationKey,
  type PublicSettings,
} from '@/lib/publicSettings';

type Value = {
  applications: PublicSettings['applications'];
  /** İstek hâlâ sürüyor mu (ilk yüklemede true). */
  loading: boolean;
};

const ApplicationSettingsContext = createContext<Value>({
  applications: undefined,
  loading: true,
});

// Uygulama ömrü boyunca tek istek — bkz. provider içindeki kullanım.
let settingsPromise: Promise<PublicSettings | undefined> | undefined;

// Başvuru açık/kapalı durumu UYGULAMA AÇILIŞINDA BİR KEZ çekilir ve context ile
// tüm sayfalara yayılır — her sayfa kendi isteğini atmaz.
export const ApplicationSettingsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [applications, setApplications] =
    useState<PublicSettings['applications']>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    // Modül seviyesinde paylaşılan promise: provider birden fazla kez mount
    // olsa da (React StrictMode geliştirmede efektleri iki kez çalıştırır)
    // istek GERÇEKTEN bir kez gider.
    settingsPromise ??= getPublicSettings().catch(() => undefined);
    settingsPromise
      // Hata durumunda applications undefined kalır → her şey AÇIK sayılır.
      // Ayar servisi düşse bile başvuru yolu kapanmasın; asıl kısıtlama zaten
      // backend'de zorlanıyor.
      .then((s) => active && setApplications(s?.applications))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  return (
    <ApplicationSettingsContext.Provider value={{ applications, loading }}>
      {children}
    </ApplicationSettingsContext.Provider>
  );
};

/**
 * Bir programın başvurusu açık mı?
 *
 * VARSAYILAN AÇIK: değer henüz yüklenmediyse, istek başarısızsa, backend o
 * anahtarı hiç göndermediyse ya da `key` verilmediyse `true` döner (sözleşme
 * gereği). Böylece geçici bir ağ sorunu yüzünden açık bir programa "kapalı"
 * denmez; tersi durumda da (gerçekten kapalıysa) backend gönderimi 403 ile
 * reddeder.
 *
 * `key` opsiyoneldir: kapatılabilir bir programa bağlı OLMAYAN CTA'lar da bu
 * hook'u koşulsuz çağırabilsin diye (React hook kuralları koşullu çağrıya izin
 * vermez).
 */
export const useApplicationOpen = (
  key: ApplicationKey | undefined,
): boolean => {
  const { applications } = useContext(ApplicationSettingsContext);
  if (!key) return true;
  return applications?.[key] !== false;
};

export const useApplicationSettings = () =>
  useContext(ApplicationSettingsContext);

export default ApplicationSettingsContext;
